import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Users, Trash2, ChevronRight, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

function SettingsRow({ to, icon: Icon, title, description, destructive, testId }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-4 rounded-md hover:bg-[hsl(var(--muted))] transition"
      data-testid={testId}
    >
      <Icon
        size={18}
        style={{
          color: destructive
            ? "hsl(var(--destructive))"
            : "hsl(var(--nvl-accent))",
        }}
      />
      <div className="flex-1">
        <div className={`text-sm font-medium ${destructive ? "text-[hsl(var(--destructive))]" : ""}`}>
          {title}
        </div>
        {description ? (
          <div className="text-xs mt-0.5" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {description}
          </div>
        ) : null}
      </div>
      <ChevronRight size={16} style={{ color: "hsl(var(--nvl-ink-soft))" }} />
    </Link>
  );
}

export default function Settings() {
  const { logout, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.nav.settings")}
      </p>
      <h1 className="mt-3 text-4xl md:text-5xl">{t("app.settings.title")}</h1>

      <div className="mt-10 nvl-card p-6 md:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">{t("app.settings.language")}</div>
            <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              IT / EN
            </div>
          </div>
          <LanguageSwitcher />
        </div>
        <div className="nvl-divider" />
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">{t("app.settings.logout")}</div>
            <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("common.slogan")}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="settings-logout-button"
            className="nvl-btn nvl-btn-outline"
          >
            {t("common.signOut")}
          </button>
        </div>
      </div>

      <div className="mt-6 nvl-card divide-y" style={{ borderColor: "hsl(var(--nvl-border))" }}>
        <SettingsRow
          to="/app/settings/invitations"
          icon={Send}
          title="Invita una persona"
          description="Condividi il tuo link personale per far conoscere NOVAXLOVE."
          testId="settings-open-invitations"
        />
        <SettingsRow
          to="/app/settings/blocked-users"
          icon={Users}
          title={t("app.moderation.blockedListTitle")}
          description={t("app.moderation.blockedListSubtitle")}
          testId="settings-open-blocked-users"
        />
        <SettingsRow
          to="/app/settings/privacy"
          icon={Shield}
          title={t("app.account.privacyTitle")}
          description={t("app.account.privacySubtitle")}
          testId="settings-open-privacy"
        />
        <SettingsRow
          to="/app/settings/delete-account"
          icon={Trash2}
          title={t("app.account.deleteTitle")}
          description={t("app.account.deleteSubtitle")}
          destructive
          testId="settings-open-delete-account"
        />
      </div>

      {user?.role === "administrator" ? (
        <div className="mt-6">
          <Link
            to="/admin/moderation"
            className="nvl-btn nvl-btn-outline text-sm"
            data-testid="settings-open-admin"
          >
            {t("app.admin.nav.moderation")}
          </Link>
        </div>
      ) : null}

      <p className="mt-8 text-xs text-center" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        <Link to="/legal/privacy" className="underline mx-2" data-testid="settings-legal-privacy">
          {t("app.legal.privacyTitle")}
        </Link>
        ·
        <Link to="/legal/terms" className="underline mx-2" data-testid="settings-legal-terms">
          {t("app.legal.termsTitle")}
        </Link>
        ·
        <Link to="/legal/cookies" className="underline mx-2" data-testid="settings-legal-cookies">
          {t("app.legal.cookiesTitle")}
        </Link>
      </p>
    </div>
  );
}
